import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Providers } from './providers'
import { AppRouter } from './router'
import { authenticateWithTelegram, clearAuth } from '@/shared/api/auth'
import { Loader2 } from 'lucide-react'

/**
 * Parse Telegram startParam and return navigation path
 * Supports formats:
 * - traffic_{resourceId} -> /traffic/{resourceId}
 * - traffic_{resourceId}_stats -> /traffic/{resourceId}/statistics
 * - bot_{botId} -> /bots/{botId}
 * - bot_{botId}_stats -> /bots/{botId}/statistics
 * - statistics -> /statistics
 * - shows -> /shows
 * - show_{showId}_stats -> /shows/{showId}/statistics
 */
function parseStartParam(startParam: string | undefined): string | null {
  if (!startParam) return null

  // Parse statistics -> /statistics (general)
  if (startParam === 'statistics') {
    return '/statistics'
  }

  if (startParam === 'shows') {
    return '/shows'
  }

  // Parse show_xxx_stats -> /shows/xxx/statistics
  if (startParam.startsWith('show_') && startParam.endsWith('_stats')) {
    const showId = startParam.slice('show_'.length, -'_stats'.length)
    if (showId) {
      return `/shows/${showId}/statistics`
    }
  }

  // Parse traffic_xxx_stats -> /traffic/xxx/statistics
  if (startParam.startsWith('traffic_') && startParam.endsWith('_stats')) {
    const resourceId = startParam.slice('traffic_'.length, -'_stats'.length)
    if (resourceId) {
      return `/traffic/${resourceId}/statistics`
    }
  }

  // Parse traffic_xxx -> /traffic/xxx
  if (startParam.startsWith('traffic_')) {
    const resourceId = startParam.slice('traffic_'.length)
    if (resourceId) {
      return `/traffic/${resourceId}`
    }
  }

  // Parse bot_xxx_stats -> /bots/xxx/statistics
  if (startParam.startsWith('bot_') && startParam.endsWith('_stats')) {
    const botId = startParam.slice('bot_'.length, -'_stats'.length)
    if (botId) {
      return `/bots/${botId}/statistics`
    }
  }

  // Parse bot_xxx -> /bots/xxx
  if (startParam.startsWith('bot_')) {
    const botId = startParam.slice('bot_'.length)
    if (botId) {
      return `/bots/${botId}`
    }
  }

  return null
}

function AppContent() {
  const navigate = useNavigate()
  const [authReady, setAuthReady] = useState(false)
  const [authError, setAuthError] = useState(false)
  const [initialPath, setInitialPath] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    const initializeApp = async () => {
      // Initialize Telegram WebApp
      const tg = window.Telegram?.WebApp
      if (tg) {
        tg.ready()
        tg.expand()

        // Parse startParam for deep linking
        const startParam = tg.initDataUnsafe?.start_param
        if (startParam) {
          console.log('[App] Received startParam:', startParam)
          const targetPath = parseStartParam(startParam)
          if (targetPath) {
            console.log('[App] Will navigate to:', targetPath)
            setInitialPath(targetPath)
          }
        }

        // Request fullscreen mode
        try {
          if (typeof tg.requestFullscreen === 'function') {
            tg.requestFullscreen()
            console.log('[App] Fullscreen mode requested')
          } else {
            console.warn('[App] requestFullscreen not supported in this Telegram version')
          }
        } catch (e) {
          console.error('[App] Failed to request fullscreen:', e)
        }

        // Disable vertical swipes (prevent pull-to-close)
        try {
          const tgWithSwipes = tg as any
          if (typeof tgWithSwipes.disableVerticalSwipes === 'function') {
            tgWithSwipes.disableVerticalSwipes()
            console.log('[App] Vertical swipes disabled')
          } else {
            console.warn('[App] disableVerticalSwipes not supported in this Telegram version')
          }
        } catch (e) {
          console.error('[App] Failed to disable vertical swipes:', e)
        }
      }

      // Apply theme based on Telegram's color scheme
      if (tg?.colorScheme === 'dark') {
        // BotFather dark blue color scheme
        document.documentElement.style.setProperty('--tg-theme-bg-color', '#17212b')
        document.documentElement.style.setProperty('--tg-theme-text-color', '#ffffff')
        document.documentElement.style.setProperty('--tg-theme-hint-color', '#8e96a0')
        document.documentElement.style.setProperty('--tg-theme-button-text-color', '#ffffff')
        document.documentElement.style.setProperty('--tg-theme-secondary-bg-color', '#242f3d')

        // Dark theme borders - same dark blue color, slightly lighter
        document.documentElement.style.setProperty('--tg-theme-section-border', '#2a3542')
        document.documentElement.style.setProperty('--tg-theme-divider-border', '#303d4d')

        console.log('[App] Applied BotFather dark theme')
      } else if (tg?.themeParams) {
        // Use light theme colors for light mode
        const theme = tg.themeParams
        if (theme.bg_color) document.documentElement.style.setProperty('--tg-theme-bg-color', theme.bg_color)
        if (theme.text_color) document.documentElement.style.setProperty('--tg-theme-text-color', theme.text_color)
        if (theme.hint_color) document.documentElement.style.setProperty('--tg-theme-hint-color', theme.hint_color)
        if (theme.button_text_color) document.documentElement.style.setProperty('--tg-theme-button-text-color', theme.button_text_color)
        if (theme.secondary_bg_color) document.documentElement.style.setProperty('--tg-theme-secondary-bg-color', theme.secondary_bg_color)

        // Light theme borders - subtle gray borders
        document.documentElement.style.setProperty('--tg-theme-section-border', 'rgba(0, 0, 0, 0.06)')
        document.documentElement.style.setProperty('--tg-theme-divider-border', 'rgba(0, 0, 0, 0.10)')

        console.log('[App] Applied Telegram light theme:', theme)
      }

      // ALWAYS force blue theme for button and link colors (ignore Telegram settings)
      // This ensures consistent branding regardless of user's Telegram accent color
      document.documentElement.style.setProperty('--tg-theme-link-color', '#5288c1')
      document.documentElement.style.setProperty('--tg-theme-button-color', '#5288c1')
      console.log('[App] Forced blue button/link colors (#5288c1)')

      clearAuth()
      console.log('[App] Authenticating with current Telegram account...')
      try {
        await authenticateWithTelegram()
        console.log('[App] Authentication successful')
        if (!cancelled) {
          setAuthReady(true)
        }
      } catch (error) {
        console.error('[App] Authentication failed:', error)
        clearAuth()
        if (!cancelled) {
          setAuthError(true)
        }
      }
    }

    initializeApp()

    return () => {
      cancelled = true
    }
  }, [])

  // Navigate to initial path after authentication completes (deep linking)
  useEffect(() => {
    if (authReady && initialPath) {
      console.log('[App] Navigating to initial path:', initialPath)
      const targetPath = initialPath
      setInitialPath(null) // Clear to prevent repeated navigation
      navigate(targetPath, { replace: true })
    }
  }, [authReady, initialPath, navigate])

  if (authError) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-tg-bg px-6 text-center text-tg-text">
        <p>Не удалось авторизоваться через Telegram.</p>
        <button
          type="button"
          className="rounded-lg bg-tg-button px-4 py-2 text-tg-button-text"
          onClick={() => window.location.reload()}
        >
          Повторить
        </button>
      </div>
    )
  }

  // Show loading screen until authentication completes
  if (!authReady) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-tg-bg">
        <Loader2 className="w-8 h-8 animate-spin text-tg-button" />
      </div>
    )
  }

  return <AppRouter />
}

function App() {
  return (
    <Providers>
      <AppContent />
    </Providers>
  )
}

export default App
